/**
 * Cloche de notifications pour la PWA étudiant.
 * 2 onglets : "Alertes" (scolarité, résultats…) + "Messages" (chat camarades + scolarité).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, MessageSquare,
  CheckCircle2, AlertTriangle, ShieldAlert, Info,
} from 'lucide-react';
import { portailApi, NotificationItem } from '../services/portailApi';

const NOTIF_ICONS: Record<string, { Icon: React.ComponentType<any>; color: string }> = {
  success: { Icon: CheckCircle2, color: 'text-emerald-500' },
  warning: { Icon: AlertTriangle, color: 'text-amber-500' },
  danger:  { Icon: ShieldAlert,  color: 'text-rose-500' },
  info:    { Icon: Info,         color: 'text-sky-500' },
};

// Une notif est de type "message" si son lien pointe vers la messagerie.
function isMessageNotif(n: NotificationItem): boolean {
  const l = n.lien || '';
  return l.startsWith('/portail/messagerie')
    || l.startsWith('/portail/messages-camarades')
    || l.startsWith('/portail/messages')
    || (l === '/portail/notifications' && n.type_notif === 'message_envoye');
}

export const NotificationsBell: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'alertes' | 'messages'>('alertes');
  const [items, setItems] = useState<NotificationItem[]>([]);

  const refresh = async () => {
    try {
      const res = await portailApi.notifications();
      setItems(res.results);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    // Fix 2026-09-07 : rafraîchir dès que l'onglet redevient visible ou
    // que la fenêtre reprend le focus, sinon le badge reste figé longtemps
    // après la lecture d'une notif depuis la page dédiée.
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const { alertes, messages, nbUnreadAlertes, nbUnreadMessages } = useMemo(() => {
    const a: NotificationItem[] = [];
    const m: NotificationItem[] = [];
    let ua = 0, um = 0;
    for (const n of items) {
      if (isMessageNotif(n)) {
        m.push(n);
        if (!n.lu) um++;
      } else {
        a.push(n);
        if (!n.lu) ua++;
      }
    }
    return { alertes: a, messages: m, nbUnreadAlertes: ua, nbUnreadMessages: um };
  }, [items]);
  const unread = nbUnreadAlertes + nbUnreadMessages;
  const list = tab === 'alertes' ? alertes : messages;

  const handleClick = async (n: NotificationItem) => {
    // Fix 2026-09-07 : marque lue en optimiste (avant l'appel réseau) pour
    // que le badge tombe immédiatement, même si `navigate` démonte ce
    // composant avant le retour du backend.
    if (!n.lu) {
      setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, lu: true } : it)));
      portailApi.marquerNotifLue(n.id).catch(() => { /* ignore */ });
    }
    setOpen(false);
    // Fix 2026-09-07 : si la notif n'a pas de lien, ouvre la page dédiée
    // au lieu de laisser l'utilisateur là où il est (impression que rien
    // ne se passe → "ça ramène en accueil").
    if (n.lien) {
      if (n.lien.startsWith('/portail/')) navigate(n.lien);
      else if (n.lien.startsWith('/')) navigate(n.lien);
      else window.location.href = n.lien;
    } else {
      navigate('/portail/notifications');
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 hover:bg-gray-800 rounded-full text-gray-300"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-full">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-gray-900 rounded-xl shadow-2xl border border-gray-800 z-50 max-h-[70vh] flex flex-col">
            <div className="p-3 border-b border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-100">Notifications</span>
                {unread > 0 && (
                  <span className="text-xs text-blue-400">{unread} non lue{unread > 1 ? 's' : ''}</span>
                )}
              </div>
              {/* Onglets Alertes / Messages */}
              <div className="flex bg-gray-800 rounded-lg p-0.5 gap-0.5">
                <button
                  onClick={() => setTab('alertes')}
                  className={`flex-1 px-3 py-1.5 rounded text-xs font-medium inline-flex items-center justify-center gap-1.5 transition-colors ${
                    tab === 'alertes' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  <Bell size={12} /> Alertes
                  {nbUnreadAlertes > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold bg-red-500 text-white rounded-full">
                      {nbUnreadAlertes > 9 ? '9+' : nbUnreadAlertes}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setTab('messages')}
                  className={`flex-1 px-3 py-1.5 rounded text-xs font-medium inline-flex items-center justify-center gap-1.5 transition-colors ${
                    tab === 'messages' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  <MessageSquare size={12} /> Messages
                  {nbUnreadMessages > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold bg-blue-500 text-white rounded-full">
                      {nbUnreadMessages > 9 ? '9+' : nbUnreadMessages}
                    </span>
                  )}
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
            {list.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">
                {tab === 'messages' ? 'Aucun nouveau message' : 'Aucune alerte'}
              </div>
            ) : (
              <div>
                {list.map((n) => {
                  const info = NOTIF_ICONS[n.type_notif] || NOTIF_ICONS.info;
                  const Icon = info.Icon;
                  return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left p-3 border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${
                      !n.lu ? 'bg-gray-800/30' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <Icon size={16} className={`${info.color} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className={`text-sm flex-1 ${!n.lu ? 'font-semibold text-gray-100' : 'text-gray-200'}`}>{n.titre}</div>
                          {!n.lu && <span className="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0" />}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                          {n.message}
                        </div>
                        <div className="text-[10px] text-gray-500 mt-1">
                          {new Date(n.date_creation).toLocaleString('fr-FR')}
                        </div>
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            )}
            </div>
            <div className="border-t border-gray-800 p-2 flex-shrink-0">
              <button
                onClick={() => {
                  setOpen(false);
                  if (tab === 'messages') navigate('/portail/messagerie');
                  else navigate('/portail/notifications');
                }}
                className="w-full py-2 text-xs font-medium text-blue-300 hover:bg-blue-900/30 rounded transition-colors"
              >
                {tab === 'messages' ? 'Ouvrir la messagerie →' : 'Voir toutes les notifications →'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
